
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Grid,
  Stack,
  Link,
  Divider,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import { useApi } from '../../../hooks/useApi';
import FileUploaderWithPreview from '../../../components/hook-form/FileUploaderWithPreview';

// ----------------------------------------------------------------------

BulletinForm.propTypes = {
  bulletin: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    document: PropTypes.any,
  }),
};

export default function BulletinForm({ bulletin }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { doSave } = useApi();

  const ModelSchema = Yup.object().shape({
    title: Yup.string().required(t('Required')),
    description: Yup.string().required(t('Required')),
  });

  const defaultValues = {
    title: '',
    description: '',
  };

  const methods = useForm({
    resolver: yupResolver(ModelSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (bulletin) {
      reset({
        title: bulletin.title || '',
        description: bulletin.description || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulletin]);

  const onSubmit = async (data) => {
    if (!(data.image instanceof File || data.image instanceof Blob)) {
      data.image = null;
    }

    const headers = { headers: { 'Content-Type': 'multipart/form-data' } };
    await doSave('bulletins', data, t('bulletins_saved'), '', bulletin, headers);
    navigate('/dashboard/bulletin/');
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        {/* LEFT: Main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ArticleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('title')}</Typography>
              </Stack>
              <RHFTextField name="title" label={t('title')} fullWidth />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('description')}</Typography>
              </Stack>
              <RHFTextField
                name="description"
                label={t('description')}
                fullWidth
                multiline
                minRows={5}
              />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PictureAsPdfIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('document') || 'Documento PDF'}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t('bulletin_pdf_hint') || 'Sube el boletín en formato PDF. Se comprimirá automáticamente.'}
              </Typography>
              <FileUploaderWithPreview name="image" accept=".pdf,application/pdf" currentUrl={bulletin?.document || ''} />
            </Box>
          </Stack>
        </Grid>

        {/* RIGHT: Actions panel */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              background: '#f8faff',
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {t('actions') || 'Acciones'}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {bulletin && (
              <Box mb={2} p={1.5} sx={{ background: '#fff', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {t('current_document') || 'Documento actual'}
                </Typography>
                {bulletin.document ? (
                  <Link href={bulletin.document} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover">
                    {t('view_document') || 'Ver documento'}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.disabled">{t('no_document') || 'Sin documento'}</Typography>
                )}
              </Box>
            )}

            <Stack spacing={1.5}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ background: '#032C65', '&:hover': { background: '#054a9e' }, borderRadius: 2 }}
              >
                {t('save')}
              </LoadingButton>
              <Link
                component={RouterLink}
                variant="body2"
                underline="hover"
                to="/dashboard/bulletin/"
                sx={{ textAlign: 'center', display: 'block', color: 'text.secondary' }}
              >
                {t('go_back')}
              </Link>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
